import { APP_BASE_HREF, Location, AsyncPipe } from "@angular/common";
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    Signal,
    computed,
    inject,
    signal,
} from "@angular/core";

import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { filter, map, Observable } from "rxjs";

import { exportedSass } from "@hermes/aphrodite-design-system-tools-exported-sass";
import { LayoutFacade } from "@hermes/aphrodite-layout-state";
import { LogoComponent } from "@hermes/aphrodite-logo";
import { SvgIconComponent } from "@hermes/aphrodite-svg";
import { LOCALE } from "@hermes/core-app-core";
import { AnalyticsService } from "@hermes/hermes-web-fragments-analytics";
import { ACCOUNT_PATH, LOGIN_PATH } from "@hermes/hermes-web-fragments-customer";
import { BasketFacade } from "@hermes/hermes-web-states-basket-state";
import {
    FeatureFlagFacade,
    HEADER_FULL_TRANSPARENT,
} from "@hermes/hermes-web-states-flipper-state";
import { TrayOpenerDirective } from "@hermes/hermes-web-states-tray-state";
import { UserFacade } from "@hermes/hermes-web-states-user-state";
import { SrOnlyNoSnippetDirective } from "@hermes/utils-generic-directives-sr-only-no-snippet";
import { RouterService } from "@hermes/utils-generic-services-router";

import { BreakpointService } from "@hermes/utils-generic-services-user-interface";

import { HeaderAccountClickEvent } from "../../events/header-account-click.event";
import { HeaderClickEvent } from "../../events/header-click.event";
import { CartLabelPipe } from "../../pipes/cart-label.pipe";
import { HeaderSearchBarComponent } from "../header-search-bar/header-search-bar.component";
import { activeMenu } from "../menu-bar/menu-bar.state";

@Component({
    selector: "h-header-bar",
    imports: [
        LogoComponent,
        TrayOpenerDirective,
        SvgIconComponent,
        HeaderSearchBarComponent,
        SrOnlyNoSnippetDirective,
        AsyncPipe,
        CartLabelPipe,
    ],
    templateUrl: "./header-bar.component.html",
    styleUrls: ["./header-bar.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderBarComponent {
    public baseHref = inject(APP_BASE_HREF);
    private locale = inject(LOCALE);
    private analyticsService = inject(AnalyticsService);
    public basketFacade = inject(BasketFacade);
    private userFacade = inject(UserFacade);
    private featureFlagFacade = inject(FeatureFlagFacade);
    private routerService = inject(RouterService);
    private location = inject(Location);
    private breakpointService = inject(BreakpointService);
    private layoutFacade = inject(LayoutFacade);
    private router = inject(Router);

    @Input({ required: true })
    public isUnderMenuBreakpoint!: boolean;

    public searchExpanded = signal(false);

    public showLogo = computed(
        () => !(this.searchExpanded() && this.isMobile()),
    );

    public accountUrl: string = ACCOUNT_PATH;
    public loginUrl: string = LOGIN_PATH;

    public isLoggedIn$: Observable<boolean> = this.userFacade.isLoggedIn$;

    public totalItems$: Observable<number> = this.basketFacade.totalItems$;

    public isMobile: Signal<boolean>;

    public isHeaderTransparent = toSignal(
        this.layoutFacade.headerTransparent$,
        { initialValue: false },
    );

    public previousTransparencyValue: boolean | undefined;
    protected readonly SECONDARY_EXTRA_LIGHT =
        exportedSass.beigeColors["color-beige-level-2"];

    public isHeaderFullTransparentFF = toSignal(
        this.featureFlagFacade.isActivated(HEADER_FULL_TRANSPARENT),
        { initialValue: false },
    );

    public isAnyParentCategoryActive = computed(() => {
        const active = activeMenu();
        return !!active && active.isActive();
    });

    public shouldShowWhiteText = computed(
        () =>
            this.isHeaderTransparent() &&
            this.isHeaderFullTransparentFF() &&
            !this.isAnyParentCategoryActive(),
    );

    private get isHomePath(): boolean {
        const path = this.location.path();
        return path === "/" || path === `${this.locale.urlPrefix}/`;
    }

    public isHomePage: Signal<boolean> = toSignal(
        this.router.events.pipe(
            filter(
                (event): event is NavigationEnd =>
                    event instanceof NavigationEnd,
            ),
            map(() => this.isHomePath),
        ),
        { initialValue: this.isHomePath },
    );

    constructor() {
        this.isMobile = this.breakpointService.isMobile;
    }

    public goToCartAndSendEvent(label: string): void {
        this.sendTopNavigationEvent(label);
        this.goToCart();
    }

    public goToCart(): void {
        // Changed to route with or without SPA depending on feature flag activation
        this.routerService.spaRedirect(`${this.locale.urlPrefix}/cart/`);
    }

    public goToPage(event: MouseEvent, path: string): void {
        event.preventDefault();
        if (this.location.path() === path) {
            // If we already are on page, we must reload it, so we use nativeRedirect
            this.routerService.nativeRedirect(
                `${this.locale.urlPrefix}${path}`,
            );
            return;
        }
        // else we use spaRedirect
        this.routerService.spaRedirect(path);
    }

    public sendAccountEvent(): void {
        this.analyticsService.sendData(
            new HeaderAccountClickEvent({
                virtualpageuri: `${this.locale.urlPrefix}/account`,
            }),
        );
    }

    public sendTopNavigationEvent(label: string): void {
        this.analyticsService.sendData(
            new HeaderClickEvent({
                eventaction: "TopNavigationHead",
                eventlabel: label,
            }),
        );
    }

    public onLogoClick(): void {
        this.analyticsService.sendData(
            new HeaderClickEvent({
                eventaction: "Logo",
            }),
        );
    }

    public onSearchExpanded(searchExpanded: boolean) {
        this.searchExpanded.set(searchExpanded);
        this.setTransparencyHeader(searchExpanded);
    }

    public setTransparencyHeader(searchExpanded: boolean): void {
        if (searchExpanded) {
            if (this.isHeaderTransparent()) {
                this.previousTransparencyValue = true;
                this.layoutFacade.setHeaderTransparency(false);
            }
        } else {
            if (this.previousTransparencyValue) {
                this.previousTransparencyValue = undefined;
                this.layoutFacade.setHeaderTransparency(true);
            }
        }
    }
}
